/**
 * Pure upper-storey structural grid for the HQ tower.
 *
 * The master spec calls for a 6 m grid, one 0.5 m column per storey, with
 * columns removed from the atrium void. The built HQ has two further
 * authoritative constraints that the prose grid does not account for:
 *
 * - the grand switchback occupies two nominal grid intersections; and
 * - the east-side circulation ring runs immediately outside the void.
 *
 * Those intersections are explicitly classified rather than silently moved.
 * East-perimeter columns remain real 0.5 m structural members; the narrower
 * bronze facade mullions sit within them as finish trim.
 */

export const HQ_STRUCTURAL_GRID_X = [-24, -18, -12, -6, 0, 6, 12, 18, 24] as const
export const HQ_STRUCTURAL_GRID_Z = [-12, -6, 0, 6, 12] as const
export const HQ_STRUCTURAL_COLUMN_SIZE = 0.5
export const HQ_STRUCTURAL_COLUMN_HEIGHT = 4.2
/** Warm graphite keeps the L1 production columns present without reading black. */
export const HQ_L1_COLUMN_FINISH_COLOR = [0.34, 0.31, 0.27] as const

export const HQ_UPPER_STRUCTURAL_FLOORS = [
  { floor: 1, walkY: 4.5 },
  { floor: 2, walkY: 9 },
  { floor: 3, walkY: 13.5 },
] as const

const ATRIUM_VOID = { minX: -3, maxX: 11, minZ: -6, maxZ: 6 } as const
const GRAND_STAIR = { minX: -8.7, maxX: -3, minZ: -6.1, maxZ: 0.8 } as const

// The Phase-4 circulation contract uses x=11.8 along the east void edge. A
// 0.3 m player capsule occupies x=11.5..12.1, so the x=12 grid line must open
// at z=-6/0/+6 instead of pinching that primary route to zero clearance.
const EAST_CIRCULATION = {
  minX: 11.5,
  maxX: 12.1,
  minZ: -6.8,
  maxZ: 6.8,
} as const

export type HqStructuralOmission =
  | 'atrium-void'
  | 'grand-stair'
  | 'east-circulation'
  | 'program-circulation'

export interface HqStructuralColumn {
  floor: 1 | 2 | 3
  walkY: number
  x: number
  z: number
}

export interface HqStructuralColumnBounds {
  minX: number
  maxX: number
  minY: number
  maxY: number
  minZ: number
  maxZ: number
}

function overlaps(
  a: { minX: number; maxX: number; minZ: number; maxZ: number },
  b: { minX: number; maxX: number; minZ: number; maxZ: number },
): boolean {
  return a.minX < b.maxX && a.maxX > b.minX &&
    a.minZ < b.maxZ && a.maxZ > b.minZ
}

function footprint(x: number, z: number): {
  minX: number
  maxX: number
  minZ: number
  maxZ: number
} {
  const half = HQ_STRUCTURAL_COLUMN_SIZE / 2
  return {
    minX: x - half,
    maxX: x + half,
    minZ: z - half,
    maxZ: z + half,
  }
}

/** Why a nominal grid point does not mint another shell-owned column. */
export function hqStructuralOmission(
  x: number,
  z: number,
): HqStructuralOmission | null {
  const box = footprint(x, z)
  if (overlaps(box, ATRIUM_VOID)) return 'atrium-void'
  if (overlaps(box, GRAND_STAIR)) return 'grand-stair'
  if (overlaps(box, EAST_CIRCULATION)) return 'east-circulation'
  return null
}

/** Storey-specific omissions where a Phase-4 route crosses a grid point. */
export function hqStructuralColumnOmission(
  floor: 1 | 2 | 3,
  x: number,
  z: number,
): HqStructuralOmission | null {
  const common = hqStructuralOmission(x, z)
  if (common) return common
  // The expanded bay lounge has one centered, repeated threshold on every
  // occupied upper floor. A nominal column at (18,0) would leave no capsule-
  // clear transition from the atrium ring into that recurring social space.
  if (x === 18 && z === 0) return 'program-circulation'
  // Both southern L2 production pods route around the void past (-6,+6).
  // Keeping this nominal column leaves less than the 0.3 m capsule radius.
  if (floor === 2 && x === -18 && (z === -6 || z === 6)) return 'program-circulation'
  return null
}

export const HQ_UPPER_STRUCTURAL_COLUMNS: readonly HqStructuralColumn[] =
  HQ_UPPER_STRUCTURAL_FLOORS.flatMap(({ floor, walkY }) =>
    HQ_STRUCTURAL_GRID_X.flatMap((x) =>
      HQ_STRUCTURAL_GRID_Z.flatMap((z) =>
        hqStructuralColumnOmission(floor, x, z) === null
          ? [{ floor, walkY, x, z }]
          : [],
      ),
    ),
  )

export function hqStructuralColumnBounds(
  column: HqStructuralColumn,
): HqStructuralColumnBounds {
  const half = HQ_STRUCTURAL_COLUMN_SIZE / 2
  return {
    minX: column.x - half,
    maxX: column.x + half,
    minY: column.walkY,
    maxY: column.walkY + HQ_STRUCTURAL_COLUMN_HEIGHT,
    minZ: column.z - half,
    maxZ: column.z + half,
  }
}
